import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
} from "@mui/material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { SIDEBAR_WIDTH } from "./PortalSidebar";
import { getLocalStorageItem } from "utils/functions";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

const PAGE_TITLES = {
  "/home": "Dashboard",
  "/family-member-table": "Family Members",
  "/yahrzeit": "Yahrzeit",
  "/classes": "Education",
  "/finance": "Billing",
  "/events-view": "Events",
  "/donation-view": "Donations",
  "/directory": "Directory",
  "/settings": "Settings",
  "/add-new-member": "Add New Member",
};

const PortalTopBar = ({ onMenuClick }) => {
  const history = useHistory();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const user = getLocalStorageItem("accountInfo", true);
  const { logoImage } = useSelector((state) => state.organization);
  const localLogoImage = localStorage.getItem("logoImg");
  const logo = logoImage || localLogoImage;
  const orgName = localStorage.getItem("orgName") || "";

  const pageTitle = PAGE_TITLES[location.pathname] || "";

  const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase();

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    handleCloseMenu();
    localStorage.removeItem("token");
    localStorage.removeItem("hasPayment");
    localStorage.removeItem("userAuthData");
    window.location.reload();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: "100%",
        top: 0,
        bgcolor: "#FFFFFF",
        borderBottom: "1px solid #E2E8F0",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: 80, px: { xs: 2, md: 3 }, gap: 2 }}>
        {/* Hamburger — mobile only */}
        <IconButton
          onClick={onMenuClick}
          edge="start"
          className="portal-hamburger"
          style={{ color: FontColors.Slate475569 }}
        >
          <MenuOutlinedIcon />
        </IconButton>

        {/* Logo + org name — always visible on desktop; on mobile only on dashboard */}
        <Box
          onClick={() => history.push("/home")}
          sx={{
            width: { md: SIDEBAR_WIDTH - 24 },
            display: { xs: location.pathname === "/home" ? "flex" : "none", sm: "none", md: "flex" },
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            flexShrink: 0,
            cursor: "pointer",
            gap: 0.25,
          }}
        >
          {logo ? (
            <img
              src={logo}
              alt="org logo"
              style={{ maxHeight: 60, maxWidth: 220, objectFit: "contain" }}
            />
          ) : (
            <Typography
              sx={{
                ...Fonts.GeneralSubHeaderFont,
                color: FontColors.Blue2555EF,
                whiteSpace: "nowrap",
              }}
            >
              Member Portal
            </Typography>
          )}
        </Box>

        {/* Divider */}
        <Box sx={{ width: "1px", height: 32, bgcolor: "#E2E8F0", display: { xs: "none", md: "block" } }} />

        {/* Org name + page title */}
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {orgName && (
            <>
              <Typography
                sx={{
                  ...Fonts.GeneralLabelFont,
                  color: FontColors.Slate64748B,
                  whiteSpace: "nowrap",
                  display: { xs: "none", sm: "block" },
                }}
              >
                {orgName}
              </Typography>
              {pageTitle && (
                <Typography sx={{ color: FontColors.SlateCBD5E1, fontSize: Fonts.GeneralLargeBodyFont.fontSize, lineHeight: 1, display: { xs: "none", md: "block" } }}>/</Typography>
              )}
            </>
          )}
          {pageTitle && (
            <Box
              sx={{
                display: { xs: location.pathname === "/home" ? "none" : "block", md: "block" },
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                component="span"
                className="portal-page-title"
                sx={{
                  ...Fonts.GeneralSubHeaderFont,
                  color: FontColors.Slate0F172A,
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {pageTitle}
              </Typography>
            </Box>
          )}
        </Box>

        {/* User avatar + dropdown */}
        <Box
          onClick={handleOpenMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            px: 1.5,
            py: 0.75,
            borderRadius: "10px",
            "&:hover": { bgcolor: "#F1F5F9" },
            transition: "background-color 0.15s ease",
          }}
        >
          <Avatar
            src={user?.profileImage || undefined}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "#2555EF",
              fontSize: Fonts.GeneralNavFont.fontSize,
              fontWeight: FontWeights.Bold,
              fontFamily: Fonts.GeneralNavFont.fontFamily,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography
              sx={{
                ...Fonts.GeneralNavFont,
                color: FontColors.Slate0F172A,
                lineHeight: 1.3,
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              sx={{
                fontSize: Fonts.GeneralCaptionFont.fontSize,
                color: FontColors.Slate64748B,
                fontFamily: Fonts.GeneralCaptionFont.fontFamily,
                lineHeight: 1.3,
              }}
            >
              {user?.primary_email_address}
            </Typography>
          </Box>
        </Box>

        {/* Dropdown menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          PaperProps={{
            elevation: 4,
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: "12px",
              overflow: "visible",
              border: "1px solid #E2E8F0",
              "& .MuiMenuItem-root": {
                fontSize: Fonts.GeneralNavFont.fontSize,
                fontFamily: Fonts.GeneralNavFont.fontFamily,
                py: 1.2,
                px: 2,
                borderRadius: "8px",
                mx: 0.5,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              sx={{
                ...Fonts.GeneralNavFont,
                color: FontColors.Slate0F172A,
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              sx={{
                fontSize: Fonts.GeneralCaptionFont.fontSize,
                color: FontColors.Slate64748B,
                fontFamily: Fonts.GeneralCaptionFont.fontFamily,
              }}
            >
              {user?.primary_email_address}
            </Typography>
          </Box>

          <Divider sx={{ my: 0.5 }} />

          <MenuItem onClick={() => { handleCloseMenu(); history.push("/settings"); }}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" sx={{ color: FontColors.Slate475569 }} />
            </ListItemIcon>
            Settings
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          <MenuItem onClick={handleLogout} sx={{ color: `${FontColors.RedEF4444} !important` }}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" sx={{ color: FontColors.RedEF4444 }} />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default PortalTopBar;
